import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { fmtCurrency } from '../../lib/localeUtils';
import { METHOD_LABELS } from './assetConstants';

export const AssetScheduleDialog = ({ assetId, onClose }) => {
  const [asset, setAsset] = useState(null);

  useEffect(() => {
    if (!assetId) return;
    setAsset(null);
    api.get(`/assets/${assetId}`).then((r) => setAsset(r.data)).catch(() => toast.error('Failed to load schedule'));
  }, [assetId]);

  const currentYear = new Date().getFullYear();
  return (
    <Dialog open={!!assetId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="asset-schedule-dialog">
        <DialogHeader><DialogTitle>{asset ? `Depreciation schedule — ${asset.name}` : 'Depreciation schedule'}</DialogTitle></DialogHeader>
        {!asset ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {METHOD_LABELS[asset.method]} over {asset.useful_life_years} years · cost {fmtCurrency(asset.cost)} · salvage {fmtCurrency(asset.salvage_value)}
              {asset.status === 'disposed' && ` · disposed ${asset.disposal_date} for ${fmtCurrency(asset.disposal_amount)} (${asset.gain_loss >= 0 ? 'gain' : 'loss'} ${fmtCurrency(Math.abs(asset.gain_loss))})`}
            </p>
            <table className="w-full text-sm border rounded-lg" data-testid="asset-schedule-table">
              <thead className="bg-muted/50 text-xs">
                <tr>{['Year', 'Opening', 'Depreciation', 'Accumulated', 'Closing'].map((h, i) => (
                  <th key={h} className={`p-2 font-medium ${i ? 'text-right' : 'text-left'}`}>{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {asset.schedule.map((r) => (
                  <tr key={r.year} className={`border-t ${r.fiscal_year === currentYear ? 'bg-primary/5 font-medium' : ''}`} data-testid={`asset-schedule-row-${r.year}`}>
                    <td className="p-2">{r.fiscal_year} <span className="text-[10px] text-muted-foreground">(Y{r.year})</span></td>
                    <td className="p-2 text-right font-mono">{fmtCurrency(r.opening_value)}</td>
                    <td className="p-2 text-right font-mono">{fmtCurrency(r.depreciation)}</td>
                    <td className="p-2 text-right font-mono">{fmtCurrency(r.accumulated)}</td>
                    <td className="p-2 text-right font-mono">{fmtCurrency(r.closing_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs">Book value today: <span className="font-semibold" data-testid="asset-schedule-nbv">{fmtCurrency(asset.net_book_value)}</span></p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
